<template>
    <AppNavbar />
    <div class="container mt-5">
      <AppHeader title="Gestión de Usuarios">
        <template #buttons>
          <button v-if="canViewRoles" class="btn btn-outline-secondary me-2" @click="irARoles">Roles</button>
          <button v-if="canCreateUser" class="btn btn-success" @click="abrirModalCrearUsuario">Crear Usuario</button>
        </template>
      </AppHeader>

  <AppFilter v-model="filtroNombre" placeholder="Buscar por nombre..." customClasses="mt-4 mb-4">
        <button class="btn me-2" :class="filtroRol === 'all' ? 'btn-success' : 'btn-outline-secondary'" @click="filtrarRol('all')">Todos los Roles</button>
        <button class="btn me-2" :class="filtroEstado === 'active' ? 'btn-success' : 'btn-outline-secondary'" @click="filtrarEstado('active')">Activos</button>
        <button class="btn" :class="filtroEstado === 'inactive' ? 'btn-success' : 'btn-outline-secondary'" @click="filtrarEstado('inactive')">Inactivos</button>
      </AppFilter>

      <!-- Tabla de Usuarios -->
      <h2>Lista de Usuarios</h2>
      <AppTable :headers="['Nombre', 'Rol', 'Sucursales', 'Cajas', 'Estado', 'Acciones']">
        <tr v-for="usuario in usuariosFiltrados" :key="usuario.id">
          <td>{{ usuario.nombre }}</td>
          <td>{{ usuario.rol }}</td>
          <td>
            <div class="small">
              <div v-if="!usuario.sucursalesResumen">Sin sucursales</div>
              <div v-else>{{ usuario.sucursalesResumen }}</div>
            </div>
          </td>
          <td>
            <div class="small">
              <div v-if="!usuario.cajasResumen">Sin cajas</div>
              <div v-else>{{ usuario.cajasResumen }}</div>
            </div>
          </td>
          <td>
            <span class="badge" :class="estadoBadgeClass(usuario.estadoCuenta)">
              {{ estadoLabel(usuario.estadoCuenta) }}
            </span>
          </td>
          <td>
            <div class="actions-wrapper">
              <button v-if="canViewUsers" class="btn btn-outline-secondary btn-sm me-1" @click="abrirModalAuditoria(usuario)">Historial</button>
              <button v-if="canEditUser" class="btn btn-primary btn-sm me-1" @click="abrirModalEditarUsuario(usuario)">✏️ Editar</button>
              <button
                v-if="canActivateUser && usuario.estadoCuenta === 'INACTIVO'"
                class="btn btn-success btn-sm me-1"
                @click="activarUsuario(usuario)"
              >
                Activar
              </button>
              <button
                v-if="canUnlockUser && usuario.estadoCuenta === 'BLOQUEADO'"
                class="btn btn-outline-success btn-sm me-1"
                @click="desbloquearUsuario(usuario)"
              >
                Desbloquear
              </button>
              <button v-if="canInactivateUser" class="btn btn-danger btn-sm me-1" @click="eliminarUsuario(usuario.id)">Inactivar</button>
              <button v-if="canResetPassword" class="btn btn-warning btn-sm" @click="abrirModalResetPassword(usuario)">🔒 Reset</button>
            </div>
          </td>
        </tr>
      </AppTable>

  <AppPagination :currentPage="currentPage" :totalPages="totalPages" @page-changed="changePage" />

      <!-- Modal Crear Usuario -->
      <div
        class="modal fade"
        id="crearUsuarioModal"
        tabindex="-1"
        aria-labelledby="crearUsuarioModalLabel"
        data-bs-backdrop="static"
        data-bs-keyboard="false"
      >
        <div class="modal-dialog modal-lg">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title" id="crearUsuarioModalLabel">Crear Usuario</h5>
              <button type="button" class="btn-close" @click="cerrarModalCrearUsuario"></button>
            </div>
            <div class="modal-body">
              <form novalidate @submit.prevent="crearUsuario">
                <div v-if="crearUsuarioError" class="alert alert-danger" role="alert">
                  {{ crearUsuarioError }}
                </div>
                <!-- Row 1: Usuario + Rol -->
                <div class="row">
                  <div class="col-md-6 mb-3">
                    <label for="usuario" class="form-label">Usuario</label>
                    <input type="text" id="usuario" v-model="nuevoUsuario.usuario" class="form-control" required>
                  </div>
                  <div class="col-md-6 mb-3">
                    <label for="rolId" class="form-label">Rol</label>
                    <select id="rolId" v-model="nuevoUsuario.rol_id" class="form-select" required>
                      <option disabled value="">Seleccione un rol</option>
                      <option v-for="r in roles" :key="r.rol_id" :value="r.rol_id">
                        {{ r.nombre_rol }}
                      </option>
                    </select>
                  </div>
                </div>

                <!-- Row 2: Nombre + Apellido -->
                <div class="row">
                  <div class="col-md-6 mb-3">
                    <label for="nombre" class="form-label">Nombre</label>
                    <input type="text" id="nombre" v-model="nuevoUsuario.nombre" class="form-control" required>
                  </div>
                  <div class="col-md-6 mb-3">
                    <label for="apellido" class="form-label">Apellido</label>
                    <input type="text" id="apellido" v-model="nuevoUsuario.apellido" class="form-control" required>
                  </div>
                </div>

                <!-- Row 3: Documento + Cargo -->
                <div class="row">
                  <div class="col-md-6 mb-3">
                    <label for="documentoIdentidad" class="form-label">Documento</label>
                    <input type="text" id="documentoIdentidad" v-model="nuevoUsuario.documento_identidad" class="form-control" required>
                  </div>
                  <div class="col-md-6 mb-3">
                    <label for="cargo" class="form-label">Cargo</label>
                    <input type="text" id="cargo" v-model="nuevoUsuario.cargo" class="form-control" required>
                  </div>
                </div>

                <!-- Row 4: Email + Celular -->
                <div class="row">
                  <div class="col-md-6 mb-3">
                    <label for="email" class="form-label">Email</label>
                    <input type="email" id="email" v-model="nuevoUsuario.email" class="form-control" required>
                  </div>
                  <div class="col-md-6 mb-3">
                    <label for="celular" class="form-label">Celular</label>
                    <input type="text" id="celular" v-model="nuevoUsuario.celular" class="form-control" required>
                  </div>
                </div>

                <!-- Row 5: Dirección -->
                <div class="row">
                  <div class="col-12 mb-3">
                    <label for="direccion" class="form-label">Dirección</label>
                    <input type="text" id="direccion" v-model="nuevoUsuario.direccion" class="form-control" required>
                  </div>
                </div>

                <!-- Row 6: Fecha de Nacimiento -->
                <div class="row">
                  <div class="col-12 mb-3">
                    <label for="fechaNacimiento" class="form-label">Fecha de Nacimiento</label>
                    <input type="text" id="fechaNacimiento" :value="nuevoUsuario.fecha_nacimiento" class="form-control" placeholder="dd/mm/aaaa" inputmode="numeric" maxlength="10" required @input="handleNuevoUsuarioFechaInput">
                  </div>
                </div>

                <div class="mb-3">
                  <label class="form-label">Sucursales habilitadas</label>
                  <div v-if="!availableSucursales.length" class="alert alert-warning mb-0">
                    No hay sucursales disponibles. Debe crear al menos un establecimiento antes de dar de alta usuarios.
                  </div>
                  <div v-else class="border rounded p-3">
                    <div v-for="sucursal in availableSucursales" :key="'create-'+sucursal.sucursal_id" class="form-check mb-2">
                      <div class="d-flex align-items-center justify-content-between gap-3 flex-wrap">
                        <label class="form-check-label">
                          <input
                            class="form-check-input me-2"
                            type="checkbox"
                            :checked="hasSucursalSelected(nuevoUsuario.sucursales, sucursal.sucursal_id)"
                            @change="toggleSucursal(nuevoUsuario.sucursales, sucursal.sucursal_id, $event.target.checked)"
                          >
                          {{ sucursal.nombre_establecimiento }} (Sucursal {{ sucursal.sucursal_id }})
                        </label>
                        <div class="form-check">
                          <input
                            class="form-check-input"
                            type="radio"
                            name="nuevoUsuarioSucursalPredeterminada"
                            :disabled="!hasSucursalSelected(nuevoUsuario.sucursales, sucursal.sucursal_id)"
                            :checked="isDefaultSucursal(nuevoUsuario.sucursales, sucursal.sucursal_id)"
                            @change="setDefaultSucursal(nuevoUsuario.sucursales, sucursal.sucursal_id)"
                          >
                          <label class="form-check-label">Predeterminada</label>
                        </div>
                      </div>
                    </div>
                  </div>
                  <small class="text-muted">El usuario no podrá operar si no tiene al menos una sucursal asignada.</small>
                </div>

                <div class="mb-3">
                  <label class="form-label">Cajas habilitadas</label>
                  <div v-if="!availableCajas.length" class="alert alert-warning mb-0">
                    No hay cajas operativas disponibles. Puede crearlas desde Mantenimiento &gt; Cajas.
                  </div>
                  <div v-else class="border rounded p-3">
                    <div v-for="caja in cajasDisponiblesParaUsuario(nuevoUsuario)" :key="'create-caja-'+caja.caja_id" class="form-check mb-2">
                      <label class="form-check-label">
                        <input
                          class="form-check-input me-2"
                          type="checkbox"
                          :checked="hasCajaSelected(nuevoUsuario.cajas, caja.caja_id)"
                          @change="toggleCaja(nuevoUsuario.cajas, caja.caja_id, $event.target.checked)"
                        >
                        {{ cajaLabel(caja) }}
                      </label>
                    </div>
                    <div v-if="!cajasDisponiblesParaUsuario(nuevoUsuario).length" class="text-muted small">
                      Seleccione primero una sucursal con cajas activas.
                    </div>
                  </div>
                  <small class="text-muted">Las cajas aplican a perfiles que operan caja; inventario y otros roles pueden quedar sin caja asignada.</small>
                </div>

                <button type="submit" class="btn btn-primary" :disabled="creandoUsuario">
                  <span v-if="creandoUsuario" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                  {{ creandoUsuario ? 'Creando…' : 'Crear' }}
                </button>
              </form>
              <div v-if="temporaryCredentialMessage" class="alert alert-warning mt-3 mb-0">
                {{ temporaryCredentialMessage }}
              </div>
            </div>
          </div>
        </div>
      </div>


      <!-- Modal Editar Usuario -->
      <div class="modal fade" id="editarUsuarioModal" tabindex="-1" aria-labelledby="editarUsuarioModalLabel">
        <div class="modal-dialog modal-lg">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title" id="editarUsuarioModalLabel">Editar Usuario</h5>
              <button type="button" class="btn-close" @click="cerrarModalEditarUsuario"></button>
            </div>
            <div class="modal-body">
              <form novalidate @submit.prevent="editarUsuario">
                <div v-if="editarUsuarioError" class="alert alert-danger" role="alert">
                  {{ editarUsuarioError }}
                </div>
                <!-- Row 1: Usuario + Rol -->
                <div class="row">
                  <div class="col-md-6 mb-3">
                    <label for="usuarioEdit" class="form-label">Usuario</label>
                    <input type="text" id="usuarioEdit" v-model="usuarioActual.usuario" class="form-control" required>
                  </div>
                  <div class="col-md-6 mb-3">
                    <label for="rolIdEdit" class="form-label">Rol</label>
                    <select id="rolIdEdit" v-model="usuarioActual.rol_id" class="form-select" required>
                      <option disabled value="">Seleccione un rol</option>
                      <option v-for="r in roles" :key="r.rol_id" :value="r.rol_id">
                        {{ r.nombre_rol }}
                      </option>
                    </select>
                  </div>
                </div>

                <!-- Row 2: Nombre + Apellido -->
                <div class="row">
                  <div class="col-md-6 mb-3">
                    <label for="nombreEdit" class="form-label">Nombre</label>
                    <input type="text" id="nombreEdit" v-model="usuarioActual.nombre" class="form-control" required>
                  </div>
                  <div class="col-md-6 mb-3">
                    <label for="apellidoEdit" class="form-label">Apellido</label>
                    <input type="text" id="apellidoEdit" v-model="usuarioActual.apellido" class="form-control" required>
                  </div>
                </div>

                <!-- Row 3: Documento + Cargo -->
                <div class="row">
                  <div class="col-md-6 mb-3">
                    <label for="documentoEdit" class="form-label">Documento</label>
                    <input type="text" id="documentoEdit" v-model="usuarioActual.documento_identidad" class="form-control" required>
                  </div>
                  <div class="col-md-6 mb-3">
                    <label for="cargoEdit" class="form-label">Cargo</label>
                    <input type="text" id="cargoEdit" v-model="usuarioActual.cargo" class="form-control" required>
                  </div>
                </div>

                <!-- Row 4: Email + Celular -->
                <div class="row">
                  <div class="col-md-6 mb-3">
                    <label for="emailEdit" class="form-label">Email</label>
                    <input type="email" id="emailEdit" v-model="usuarioActual.email" class="form-control" required>
                  </div>
                  <div class="col-md-6 mb-3">
                    <label for="celularEdit" class="form-label">Celular</label>
                    <input type="text" id="celularEdit" v-model="usuarioActual.celular" class="form-control" required>
                  </div>
                </div>

                <!-- Row 5: Dirección + Fecha de Nacimiento -->
                <div class="row">
                  <div class="col-md-6 mb-3">
                    <label for="direccionEdit" class="form-label">Dirección</label>
                    <input type="text" id="direccionEdit" v-model="usuarioActual.direccion" class="form-control" required>
                  </div>
                  <div class="col-md-6 mb-3">
                    <label for="fechaNacimientoEdit" class="form-label">Fecha de Nacimiento</label>
                    <input type="text" id="fechaNacimientoEdit" :value="usuarioActual.fecha_nacimiento" class="form-control" placeholder="dd/mm/aaaa" inputmode="numeric" maxlength="10" required @input="handleUsuarioActualFechaInput">
                  </div>
                </div>

                <div class="mb-3">
                  <label class="form-label">Sucursales habilitadas</label>
                  <div v-if="!availableSucursales.length" class="alert alert-warning mb-0">
                    No hay sucursales disponibles.
                  </div>
                  <div v-else class="border rounded p-3">
                    <div v-for="sucursal in availableSucursales" :key="'edit-'+sucursal.sucursal_id" class="form-check mb-2">
                      <div class="d-flex align-items-center justify-content-between gap-3 flex-wrap">
                        <label class="form-check-label">
                          <input
                            class="form-check-input me-2"
                            type="checkbox"
                            :checked="hasSucursalSelected(usuarioActual.sucursales, sucursal.sucursal_id)"
                            @change="toggleSucursal(usuarioActual.sucursales, sucursal.sucursal_id, $event.target.checked)"
                          >
                          {{ sucursal.nombre_establecimiento }} (Sucursal {{ sucursal.sucursal_id }})
                        </label>
                        <div class="form-check">
                          <input
                            class="form-check-input"
                            type="radio"
                            name="usuarioActualSucursalPredeterminada"
                            :disabled="!hasSucursalSelected(usuarioActual.sucursales, sucursal.sucursal_id)"
                            :checked="isDefaultSucursal(usuarioActual.sucursales, sucursal.sucursal_id)"
                            @change="setDefaultSucursal(usuarioActual.sucursales, sucursal.sucursal_id)"
                          >
                          <label class="form-check-label">Predeterminada</label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="mb-3">
                  <label class="form-label">Cajas habilitadas</label>
                  <div v-if="!availableCajas.length" class="alert alert-warning mb-0">
                    No hay cajas operativas disponibles.
                  </div>
                  <div v-else class="border rounded p-3">
                    <div v-for="caja in cajasDisponiblesParaUsuario(usuarioActual)" :key="'edit-caja-'+caja.caja_id" class="form-check mb-2">
                      <label class="form-check-label">
                        <input
                          class="form-check-input me-2"
                          type="checkbox"
                          :checked="hasCajaSelected(usuarioActual.cajas, caja.caja_id)"
                          @change="toggleCaja(usuarioActual.cajas, caja.caja_id, $event.target.checked)"
                        >
                        {{ cajaLabel(caja) }}
                      </label>
                    </div>
                    <div v-if="!cajasDisponiblesParaUsuario(usuarioActual).length" class="text-muted small">
                      Seleccione primero una sucursal con cajas activas.
                    </div>
                  </div>
                  <small class="text-muted">Si el usuario no opera caja, puede quedar sin cajas asignadas.</small>
                </div>

                <button type="submit" class="btn btn-primary">Guardar</button>
              </form>
            </div>
          </div>
        </div>
      </div>


      <!-- Modal Reset Password -->
      <div class="modal fade" id="resetPasswordModal" tabindex="-1" aria-labelledby="resetPasswordModalLabel">
        <div class="modal-dialog">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title" id="resetPasswordModalLabel">Reset Password</h5>
              <button type="button" class="btn-close" @click="cerrarModalResetPassword" aria-label="Close"></button>
            </div>
            <div class="modal-body">
              <form @submit.prevent="resetPasswordAPI">
                <div class="alert alert-secondary">
                  Se generará una nueva credencial temporal. Si el usuario estaba inactivo o bloqueado, quedará habilitado nuevamente en estado pendiente para completar el primer ingreso.
                </div>
                <div v-if="resetPassword.generatedPassword" class="alert alert-warning">
                  Credencial temporal generada: <strong>{{ resetPassword.generatedPassword }}</strong>
                </div>
                <button type="submit" class="btn btn-primary">Reset</button>
              </form>
            </div>
          </div>
        </div>
      </div>

      <div class="modal fade" id="auditUserModal" tabindex="-1" aria-labelledby="auditUserModalLabel">
        <div class="modal-dialog modal-lg">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title" id="auditUserModalLabel">Historial de usuario</h5>
              <button type="button" class="btn-close" @click="cerrarModalAuditoria" aria-label="Close"></button>
            </div>
            <div class="modal-body">
              <p v-if="auditTrail.userLabel" class="mb-3">
                <strong>{{ auditTrail.userLabel }}</strong>
              </p>
              <div v-if="auditTrail.loading" class="text-muted">Cargando historial...</div>
              <div v-else-if="!auditTrail.items.length" class="alert alert-light mb-0">
                No hay eventos de auditoría para este usuario.
              </div>
              <div v-else class="table-responsive">
                <table class="table table-sm align-middle">
                  <thead>
                    <tr>
                      <th>Fecha</th>
                      <th>Descripción</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="item in auditTrail.items" :key="item.actividad_id">
                      <td>{{ formatAuditDate(item.fecha) }}</td>
                      <td>{{ item.descripcion }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>




    </div>
</template>
  
<script>
  import AppNavbar from '../components/AppNavbar.vue';
  import AppHeader from '../components/AppHeader.vue';
  import AppFilter from '../components/AppFilter.vue';
  import AppTable from '../components/AppTable.vue';
  import AppPagination from '../components/AppPagination.vue';
  import apiService from '../services/apiService';
  import operationalContextService from '@/services/operationalContextService';
  import { hasPermission } from '../services/authSessionService';
  import { Modal } from 'bootstrap';
  import { applyDateMask, toApiDate, toDisplayDate } from '../utils/date';



  export default {
    name: 'UserManagement',
    components: {
    AppNavbar,
    AppHeader,
    AppFilter,
    AppTable,
    AppPagination,
    },
    data() {
      return {
        filtroNombre: '',
        filtroRol: 'all',
        filtroEstado: 'all',
        usuarios: [], // Datos de usuarios
        allUsuarios: [], // Cache de todos los usuarios para filtrar
        roles: [],
        availableSucursales: [],
        availableCajas: [],
        currentPage: 1,
        itemsPerPage: 10,
        nuevoUsuario: {
          usuario: '',
          rol_id: null,
          nombre: '',
          apellido: '',
          documento_identidad: '',
          cargo: '',
          email: '',
          celular: '',
          direccion: '',
          fecha_nacimiento: '',
          sucursales: [],
          cajas: []
        },
        usuarioActual: {
          id: null,              // almacenará usuario_id
          usuario: '',
          rol_id: null,
          nombre: '',
          apellido: '',
          documento_identidad: '',
          cargo: '',
          email: '',
          celular: '',
          direccion: '',
          fecha_nacimiento: '',
          sucursales: [],
          cajas: [],
          activo: true,
          estado_cuenta: ''
        },
        resetPassword: {
          userId: null,
          generatedPassword: ''
        },
        temporaryCredentialMessage: '',
        crearUsuarioError: '',
        // Traba de envío en curso: sin esto cada clic en "Crear" dispara un
        // POST nuevo. El 2026-08-19 se vieron dos altas idénticas en el mismo
        // segundo, con sus errores apilados en pantalla. Con un alta que
        // fallaba era sólo ruido; con una que funciona, son dos usuarios.
        creandoUsuario: false,
        editarUsuarioError: '',
        auditTrail: {
          userId: null,
          userLabel: '',
          loading: false,
          items: []
        }
      };
    },
    computed: {
      usuariosFiltrados() {
        let filtered = Array.isArray(this.allUsuarios) ? this.allUsuarios : [];
        
        // Filtrar por nombre
        if (this.filtroNombre && this.filtroNombre.trim() !== '') {
          filtered = filtered.filter(user => user.nombre.toLowerCase().includes(this.filtroNombre.toLowerCase()));
        }
        
        // Filtrar por rol
        if (this.filtroRol !== 'all') {
          filtered = filtered.filter(user => user.rol === this.filtroRol);
        }
        
        // Filtrar por estado (activo/inactivo)
        if (this.filtroEstado !== 'all') {
          filtered = filtered.filter(user => user.status === this.filtroEstado);
        }
        
        return filtered.slice((this.currentPage - 1) * this.itemsPerPage, this.currentPage * this.itemsPerPage);
      },
      totalPages() {
        let filtered = Array.isArray(this.allUsuarios) ? this.allUsuarios : [];
        
        if (this.filtroNombre && this.filtroNombre.trim() !== '') {
          filtered = filtered.filter(u => u.nombre.toLowerCase().includes(this.filtroNombre.toLowerCase()));
        }
        if (this.filtroRol !== 'all') {
          filtered = filtered.filter(u => u.rol === this.filtroRol);
        }
        if (this.filtroEstado !== 'all') {
          filtered = filtered.filter(u => u.status === this.filtroEstado);
        }
        return Math.ceil(filtered.length / this.itemsPerPage);
      },
      canCreateUser() {
        return hasPermission('crear_usuario');
      },
      canViewUsers() {
        return hasPermission('ver_usuarios');
      },
      canEditUser() {
        return hasPermission('editar_usuario');
      },
      canInactivateUser() {
        return hasPermission('inactivar_usuario');
      },
      canActivateUser() {
        return hasPermission('inactivar_usuario');
      },
      canResetPassword() {
        return hasPermission('restablecer_contrasena');
      },
      canUnlockUser() {
        return hasPermission('desbloquear_usuario');
      },
      canViewRoles() {
        return hasPermission('ver_roles');
      }
    },
    methods: {
      handleNuevoUsuarioFechaInput(event) {
        this.nuevoUsuario.fecha_nacimiento = applyDateMask(event.target.value);
      },
      handleUsuarioActualFechaInput(event) {
        this.usuarioActual.fecha_nacimiento = applyDateMask(event.target.value);
      },
      resolveErrorMessage(error, fallback) {
        return error?.userMessage
          || error?.normalized?.message
          || error?.response?.data?.message
          || error?.response?.data?.error
          || fallback;
      },
      notifyError(message) {
        if (window.alert?.error) {
          window.alert.error(message);
        } else {
          window.alert(message);
        }
      },
      validateUsuarioForm(usuario, mode = 'crear') {
        const requiredFields = [
          ['usuario', 'Usuario'],
          ['rol_id', 'Rol'],
          ['nombre', 'Nombre'],
          ['apellido', 'Apellido'],
          ['documento_identidad', 'Documento'],
          ['cargo', 'Cargo'],
          ['email', 'Email'],
          ['celular', 'Celular'],
          ['direccion', 'Dirección'],
          ['fecha_nacimiento', 'Fecha de nacimiento']
        ];
        const missing = requiredFields
          .filter(([field]) => !String(usuario?.[field] ?? '').trim())
          .map(([, label]) => label);
        const sucursales = this.buildSucursalesPayload(usuario?.sucursales || []);
        const cajas = this.buildCajasPayload(usuario?.cajas || [], sucursales);

        if (!sucursales.length) {
          missing.push('Sucursal habilitada');
        } else if (!sucursales.some((item) => item.predeterminada)) {
          missing.push('Sucursal predeterminada');
        }
        if (this.roleRequiresCashbox(usuario?.rol_id) && !cajas.length) {
          missing.push('Caja habilitada para rol de caja');
        }

        if (usuario?.email && !/\S+@\S+\.\S+/.test(String(usuario.email).trim())) {
          const message = 'El email ingresado no es válido.';
          if (mode === 'crear') this.crearUsuarioError = message;
          if (mode === 'editar') this.editarUsuarioError = message;
          this.notifyError(message);
          return false;
        }

        if (!missing.length) {
          if (mode === 'crear') this.crearUsuarioError = '';
          if (mode === 'editar') this.editarUsuarioError = '';
          return true;
        }

        const message = `Complete los campos obligatorios: ${missing.join(', ')}.`;
        if (mode === 'crear') this.crearUsuarioError = message;
        if (mode === 'editar') this.editarUsuarioError = message;
        this.notifyError(message);
        return false;
      },
      abrirModalCrearUsuario() {
        this.nuevoUsuario = {
          usuario: '',
          rol_id: null,
          nombre: '',
          apellido: '',
          documento_identidad: '',
          cargo: '',
          email: '',
          celular: '',
          direccion: '',
          fecha_nacimiento: '',
          sucursales: [],
          cajas: []
        };
        this.temporaryCredentialMessage = '';
        this.crearUsuarioError = '';
        const modalElement = document.getElementById('crearUsuarioModal');
        const bsModal = new Modal(modalElement, {
          backdrop: 'static',
          keyboard: false
        });
        bsModal.show();
      },
      getRoles() {
        apiService.get('/api/roles')
          .then(response => {
            this.roles = response.data?.data || [];
          })
          .catch(err => console.error('Error al cargar roles:', err));
      },
      getOperationalContext() {
        operationalContextService.getUserOperationalContext()
          .then((context) => {
            this.availableSucursales = Array.isArray(context?.sucursales) ? context.sucursales : [];
            this.availableCajas = Array.isArray(context?.cajas) ? context.cajas : [];
            if (this.allUsuarios.length) {
              this.getUsuarios();
            }
          })
          .catch((error) => {
            console.error('Error al cargar sucursales operativas:', error);
            this.availableSucursales = [];
            this.availableCajas = [];
            this.notifyError(this.resolveErrorMessage(error, 'No se pudo cargar el contexto de sucursales.'));
          });
      },
      obtenerUsuarioPorId(id) {
        return apiService.get(`/api/auth/users/${id}`)
          .then(response => {
            const u = response.data;
            return {
              id: u.usuario_id,
              usuario: u.usuario,
              rol_id: u.rol_id,
              nombre: u.nombre,
              apellido: u.apellido,
              email: u.email,
              celular: u.celular,
              direccion: u.direccion,
              documento_identidad: u.documento_identidad,
              cargo: u.cargo,
              // Convertimos la fecha ISO a “YYYY-MM-DD” para que funcione en el <input type="date">
              fecha_nacimiento: toDisplayDate(u.fecha_nacimiento),
              sucursales: this.normalizeSucursales(u.sucursales),
              cajas: this.normalizeCajas(u.cajas),
              activo: u.activo,
              estado_cuenta: u.estado_cuenta
            };
          });
        },
      normalizeSucursales(sucursales = []) {
        return (Array.isArray(sucursales) ? sucursales : [])
          .map((item) => ({
            sucursal_id: Number(item.sucursal_id),
            predeterminada: item.predeterminada === true,
            activo: item.activo !== false
          }))
          .filter((item) => Number.isFinite(item.sucursal_id) && item.sucursal_id > 0);
      },
      normalizeCajas(cajas = []) {
        return (Array.isArray(cajas) ? cajas : [])
          .map((item) => ({
            caja_id: Number(item.caja_id ?? item),
            sucursal_id: item.sucursal_id != null ? Number(item.sucursal_id) : null,
            activo: item.activo !== false
          }))
          .filter((item) => Number.isFinite(item.caja_id) && item.caja_id > 0);
      },
      hasSucursalSelected(collection = [], sucursalId) {
        return this.normalizeSucursales(collection).some((item) => Number(item.sucursal_id) === Number(sucursalId));
      },
      isDefaultSucursal(collection = [], sucursalId) {
        return this.normalizeSucursales(collection).some((item) => Number(item.sucursal_id) === Number(sucursalId) && item.predeterminada === true);
      },
      toggleSucursal(collection, sucursalId, checked) {
        const normalizedId = Number(sucursalId);
        const target = Array.isArray(collection) ? collection : [];
        const currentIndex = target.findIndex((item) => Number(item.sucursal_id) === normalizedId);
        if (checked && currentIndex === -1) {
          target.push({
            sucursal_id: normalizedId,
            predeterminada: target.length === 0,
            activo: true
          });
        }
        if (!checked && currentIndex >= 0) {
          const wasDefault = target[currentIndex]?.predeterminada === true;
          target.splice(currentIndex, 1);
          if (wasDefault && target.length) {
            target[0].predeterminada = true;
          }
        }
        this.pruneCajasFueraDeSucursales();
      },
      setDefaultSucursal(collection, sucursalId) {
        const normalizedId = Number(sucursalId);
        const target = Array.isArray(collection) ? collection : [];
        target.forEach((item) => {
          item.predeterminada = Number(item.sucursal_id) === normalizedId;
        });
      },
      buildSucursalesPayload(collection = []) {
        const normalized = this.normalizeSucursales(collection);
        if (normalized.length && !normalized.some((item) => item.predeterminada)) {
          normalized[0].predeterminada = true;
        }
        return normalized;
      },
      hasCajaSelected(collection = [], cajaId) {
        return this.normalizeCajas(collection).some((item) => Number(item.caja_id) === Number(cajaId));
      },
      toggleCaja(collection, cajaId, checked) {
        const normalizedId = Number(cajaId);
        const target = Array.isArray(collection) ? collection : [];
        const currentIndex = target.findIndex((item) => Number(item.caja_id) === normalizedId);
        if (checked && currentIndex === -1) {
          const caja = this.availableCajas.find((item) => Number(item.caja_id) === normalizedId);
          target.push({
            caja_id: normalizedId,
            sucursal_id: caja?.sucursal_id ? Number(caja.sucursal_id) : null,
            activo: true
          });
        }
        if (!checked && currentIndex >= 0) {
          target.splice(currentIndex, 1);
        }
      },
      buildCajasPayload(collection = [], sucursales = []) {
        const allowedSucursalIds = new Set(this.normalizeSucursales(sucursales).map((item) => Number(item.sucursal_id)));
        return this.normalizeCajas(collection)
          .filter((item) => {
            const caja = this.availableCajas.find((row) => Number(row.caja_id) === Number(item.caja_id));
            return caja && allowedSucursalIds.has(Number(caja.sucursal_id));
          })
          .map((item) => ({ caja_id: Number(item.caja_id), activo: true }));
      },
      cajasDisponiblesParaUsuario(usuario = {}) {
        const sucursalIds = new Set(this.normalizeSucursales(usuario?.sucursales).map((item) => Number(item.sucursal_id)));
        return this.availableCajas.filter((caja) =>
          sucursalIds.has(Number(caja.sucursal_id)) &&
          caja.activo !== false &&
          String(caja.estado || 'ACTIVA').toUpperCase() === 'ACTIVA'
        );
      },
      pruneCajasFueraDeSucursales() {
        for (const usuario of [this.nuevoUsuario, this.usuarioActual]) {
          const allowedSucursalIds = new Set(this.normalizeSucursales(usuario.sucursales).map((item) => Number(item.sucursal_id)));
          if (!Array.isArray(usuario.cajas)) continue;
          usuario.cajas = usuario.cajas.filter((item) => {
            const caja = this.availableCajas.find((row) => Number(row.caja_id) === Number(item.caja_id));
            return caja && allowedSucursalIds.has(Number(caja.sucursal_id));
          });
        }
      },
      cajaLabel(caja) {
        const sucursal = this.availableSucursales.find((item) => Number(item.sucursal_id) === Number(caja.sucursal_id));
        const point = caja.codigo_punto_expedicion ? ` · Punto ${caja.codigo_punto_expedicion}` : '';
        return `${caja.codigo_caja || 'Caja'} - ${caja.nombre || ''} · ${sucursal?.nombre_establecimiento || `Sucursal ${caja.sucursal_id}`}${point}`;
      },
      formatCajasResumen(cajas = []) {
        const normalized = this.normalizeCajas(cajas);
        if (!normalized.length) return '';
        return normalized.map((item) => {
          const caja = this.availableCajas.find((row) => Number(row.caja_id) === Number(item.caja_id));
          return caja ? `${caja.codigo_caja} ${caja.nombre}`.trim() : `Caja ${item.caja_id}`;
        }).join(', ');
      },
      roleRequiresCashbox(roleId) {
        const role = this.roles.find((item) => Number(item.rol_id) === Number(roleId));
        const name = String(role?.nombre_rol || '').toLowerCase();
        return name.includes('caj') || name.includes('tesorer');
      },
      formatSucursalesResumen(sucursales = []) {
        const normalized = this.normalizeSucursales(sucursales);
        if (!normalized.length) return '';
        return normalized.map((item) => {
          const match = this.availableSucursales.find((row) => Number(row.sucursal_id) === Number(item.sucursal_id));
          const label = match?.nombre_establecimiento || `Sucursal ${item.sucursal_id}`;
          return item.predeterminada ? `${label} (Pred.)` : label;
        }).join(', ');
      },

      cerrarModalCrearUsuario() {
        const modalElement = document.getElementById('crearUsuarioModal');
        const bsModal = Modal.getInstance(modalElement);
        this.crearUsuarioError = '';
        bsModal.hide();
      },
      irARoles() {
        this.$router.push({ name: 'Roles' });
      },
      getUsuarios() {
        apiService.get('/api/auth/users')
          .then(response => {
            // Manejar diferentes estructuras de respuesta
            const data = Array.isArray(response.data) 
              ? response.data 
              : (Array.isArray(response.data?.data) ? response.data.data : []);
            
            // Mapear cada objeto recibido al formato que espera la interfaz
            const mappedUsuarios = data.map(u => ({
              id: u.usuario_id,
              nombre: u.nombre,
              rol: u.nombre_rol,
              status: u.activo ? 'active' : 'inactive',
              estadoCuenta: u.estado_cuenta,
              sucursales: this.normalizeSucursales(u.sucursales),
              cajas: this.normalizeCajas(u.cajas),
              sucursalesResumen: this.formatSucursalesResumen(u.sucursales),
              cajasResumen: this.formatCajasResumen(u.cajas)
            }));
            
            // Almacenar en allUsuarios para filtrar localmente
            this.allUsuarios = mappedUsuarios;
            this.usuarios = [...mappedUsuarios];
          })
          .catch(error => {
            console.error('Error al cargar usuarios:', error);
            this.allUsuarios = [];
            this.usuarios = [];
          });
      },
      abrirModalResetPassword(usuario) {
        this.resetPassword.userId = usuario.id;
        this.resetPassword.generatedPassword = '';
        const modalEl = document.getElementById('resetPasswordModal');
        new Modal(modalEl).show();
      },
      abrirModalAuditoria(usuario) {
        this.auditTrail = {
          userId: usuario.id,
          userLabel: usuario.nombre,
          loading: true,
          items: []
        };
        const modalEl = document.getElementById('auditUserModal');
        new Modal(modalEl).show();
        apiService.get(`/api/auth/users/${usuario.id}/audit`)
          .then((response) => {
            const user = response.data?.user;
            this.auditTrail.userLabel = user
              ? `${user.nombre || ''} ${user.apellido || ''} (${user.usuario})`.trim()
              : usuario.nombre;
            this.auditTrail.items = Array.isArray(response.data?.data) ? response.data.data : [];
          })
          .catch((error) => {
            console.error('Error al obtener auditoría del usuario:', error);
            this.auditTrail.items = [];
            this.notifyError(this.resolveErrorMessage(error, 'No se pudo obtener el historial del usuario.'));
          })
          .finally(() => {
            this.auditTrail.loading = false;
          });
      },
      cerrarModalAuditoria() {
        const modalEl = document.getElementById('auditUserModal');
        const modal = Modal.getInstance(modalEl);
        if (modal) modal.hide();
      },

       cerrarModalResetPassword() {
        const modalEl = document.getElementById('resetPasswordModal');
        Modal.getInstance(modalEl).hide();
      },

      resetPasswordAPI() {
        apiService
          .put(`/api/auth/users/${this.resetPassword.userId}/reset-password`, {})
          .then((response) => {
            this.resetPassword.generatedPassword = response.data?.password_temporal || '';
            this.getUsuarios();
          })
          .catch(error => {
            console.error('Error al resetear contraseña:', error);
            this.notifyError(this.resolveErrorMessage(error, 'No se pudo generar la credencial temporal.'));
          });
      },
      async desbloquearUsuario(usuario) {
        const confirmed = window.confirm(`¿Desea desbloquear al usuario ${usuario.nombre}?`);
        if (!confirmed) return;

        try {
          await apiService.put('/api/auth/users/unlock', { usuario_id: usuario.id });
          this.getUsuarios();
          window.alert('Usuario desbloqueado correctamente.');
        } catch (error) {
          console.error('Error al desbloquear usuario:', error);
          this.notifyError(this.resolveErrorMessage(error, 'No se pudo desbloquear el usuario.'));
        }
      },
      async activarUsuario(usuario) {
        const confirmed = window.confirm(`¿Desea activar al usuario ${usuario.nombre}?`);
        if (!confirmed) return;

        try {
          await apiService.put('/api/auth/users/activate', { usuario_id: usuario.id });
          this.getUsuarios();
          window.alert('Usuario activado correctamente.');
        } catch (error) {
          console.error('Error al activar usuario:', error);
          this.notifyError(this.resolveErrorMessage(error, 'No se pudo activar el usuario.'));
        }
      },
      estadoLabel(estado) {
        return {
          ACTIVO: 'Activo',
          PENDIENTE_ACTIVACION: 'Pendiente',
          BLOQUEADO: 'Bloqueado',
          INACTIVO: 'Inactivo'
        }[estado] || estado || 'Sin estado';
      },
      estadoBadgeClass(estado) {
        return {
          ACTIVO: 'bg-success',
          PENDIENTE_ACTIVACION: 'bg-warning text-dark',
          BLOQUEADO: 'bg-danger',
          INACTIVO: 'bg-secondary'
        }[estado] || 'bg-secondary';
      },
      formatAuditDate(value) {
        if (!value) return 'Sin fecha';
        return new Date(value).toLocaleString('es-PY');
      },

      changePage(page) {
        this.currentPage = page;
      },
      filtrarRol(rol) {
        this.filtroRol = rol;
        this.filtroEstado = 'all';
      },
      filtrarEstado(estado) {
        this.filtroEstado = estado;
        this.filtroRol = 'all'; // Resetear filtro de rol al seleccionar estado
      },
      abrirModalEditarUsuario(usuarioListado) {
        this.editarUsuarioError = '';
        this.obtenerUsuarioPorId(usuarioListado.id)
          .then(usuarioCompleto => {
            this.usuarioActual = usuarioCompleto;
            const modalEl = document.getElementById('editarUsuarioModal');
            const bsModal = new Modal(modalEl, {});
            bsModal.show();
          })
          .catch(err => {
            console.error('Error al cargar datos del usuario:', err);
            this.notifyError(this.resolveErrorMessage(err, 'No se pudieron cargar los datos del usuario.'));
          });
      },
      cerrarModalEditarUsuario() {
        const modalElement = document.getElementById('editarUsuarioModal');
        const bsModal = Modal.getInstance(modalElement);
        this.editarUsuarioError = '';
        if (bsModal) bsModal.hide();
      },

      crearUsuario() {
        if (this.creandoUsuario) return;
        if (!this.validateUsuarioForm(this.nuevoUsuario, 'crear')) return;

        const payload = {
          usuario: this.nuevoUsuario.usuario,
          rol_id: this.nuevoUsuario.rol_id,
          nombre: this.nuevoUsuario.nombre,
          apellido: this.nuevoUsuario.apellido,
          documento_identidad: this.nuevoUsuario.documento_identidad,
          cargo: this.nuevoUsuario.cargo,
          email: this.nuevoUsuario.email,
          celular: this.nuevoUsuario.celular,
          direccion: this.nuevoUsuario.direccion,
          fecha_nacimiento: toApiDate(this.nuevoUsuario.fecha_nacimiento) || null,
          sucursales: this.buildSucursalesPayload(this.nuevoUsuario.sucursales),
          cajas: this.buildCajasPayload(this.nuevoUsuario.cajas, this.nuevoUsuario.sucursales)
        };
        this.creandoUsuario = true;
        apiService.post('/api/auth/register', payload)
          .then((response) => {
            this.getUsuarios();   // recargamos la lista
            this.temporaryCredentialMessage = `Credencial temporal generada para ${this.nuevoUsuario.usuario}: ${response.data?.password_temporal || 'sin dato'}`;
            this.crearUsuarioError = '';
            this.nuevoUsuario = {
              usuario: '',
              rol_id: null,
              nombre: '',
              apellido: '',
              documento_identidad: '',
              cargo: '',
              email: '',
              celular: '',
              direccion: '',
              fecha_nacimiento: '',
              sucursales: [],
              cajas: []
            };
          })
          .catch(error => {
            console.error('Error al crear usuario:', error);
            this.crearUsuarioError = this.resolveErrorMessage(error, 'No se pudo crear el usuario.');
            this.notifyError(this.crearUsuarioError);
          })
          .finally(() => {
            this.creandoUsuario = false;
          });
      },
      editarUsuario() {
        if (!this.validateUsuarioForm(this.usuarioActual, 'editar')) return;

        const payload = {
          rol_id: this.usuarioActual.rol_id,
          nombre: this.usuarioActual.nombre,
          apellido: this.usuarioActual.apellido,
          documento_identidad: this.usuarioActual.documento_identidad,
          cargo: this.usuarioActual.cargo,
          email: this.usuarioActual.email,
          celular: this.usuarioActual.celular,
          direccion: this.usuarioActual.direccion,
          fecha_nacimiento: toApiDate(this.usuarioActual.fecha_nacimiento) || null,
          sucursales: this.buildSucursalesPayload(this.usuarioActual.sucursales),
          cajas: this.buildCajasPayload(this.usuarioActual.cajas, this.usuarioActual.sucursales)
        };
        apiService.put(`/api/auth/users/${this.usuarioActual.id}`, payload)
          .then(() => {
            this.getUsuarios();       // recargamos la lista completa
            this.editarUsuarioError = '';
            this.cerrarModalEditarUsuario();
          })
          .catch(error => {
            console.error('Error al editar usuario:', error);
            this.editarUsuarioError = this.resolveErrorMessage(error, 'No se pudo actualizar el usuario.');
            this.notifyError(this.editarUsuarioError);
          });
      },
      async eliminarUsuario(id) {
        const confirmed = window.confirm('¿Está seguro de inactivar este usuario?');
        if (!confirmed) return;

        try {
          await apiService.put('/api/auth/users/desactivate', { usuario_id: id });
          this.getUsuarios();
          window.alert('Usuario inactivado correctamente.');
        } catch (error) {
          console.error('Error al desactivar usuario:', error);
          this.notifyError(this.resolveErrorMessage(error, 'No se pudo inactivar el usuario.'));
        }
      }

    },
    mounted() {
      this.getRoles();
      this.getOperationalContext();
      this.getUsuarios();
    }
  };
</script>

<style scoped>


</style>

<style scoped>
/* Estilos generales consistentes con otras pantallas */
.container {
  background-color: #ffffff;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}
h1, h2 {
  color: #343a40;
}
.form-label {
  font-weight: bold;
  color: #495057;
}

/* Alinear y espaciar acciones de la tabla */
.actions-wrapper {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
</style>
